import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import { auth } from '../firebase/config';
import { completePasswordlessSignIn, getCurrentUser } from '../firebase/authService';
import './AuthAction.css';

/**
 * AuthAction Component
 * Handles Firebase authentication action URLs for passwordless authentication:
 * - Email verification
 * - Email sign-in links (passwordless)
 */
const AuthAction = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing your request...');

  const mode = searchParams.get('mode');
  const actionCode = searchParams.get('oobCode');
  // lang parameter available but not currently used
  // const lang = searchParams.get('lang') || 'en';

  useEffect(() => {
    const handleAuthAction = async () => {
      // Check if user is already signed in
      const currentUser = getCurrentUser();
      
      // If user is already signed in, show appropriate message
      if (currentUser) {
        setStatus('info');
        
        // Customize message based on mode
        if (mode === 'verifyEmail') {
          setMessage('Your email is already verified and you\'re signed in.');
        } else if (mode === 'signIn' || !mode) {
          setMessage(`You're already signed in as ${currentUser.email}.`);
        } else {
          setMessage(`You're already signed in as ${currentUser.email}.`);
        }
        
        // Clear the emailForSignIn from localStorage to prevent prompts
        window.localStorage.removeItem('emailForSignIn');
        return;
      }

      // For sign-in links, we don't need an oobCode in the URL
      // The entire URL is used as the sign-in link
      if (!actionCode && mode !== 'signIn') {
        setStatus('error');
        setMessage('Invalid or missing action code.');
        return;
      }

      try {
        switch (mode) {
          case 'verifyEmail':
            await handleVerifyEmail();
            break;
          case 'signIn':
            await handleSignIn();
            break;
          default:
            // If no mode is specified, check if this is a sign-in link
            await handleSignIn();
        }
      } catch (error) {
        console.error('Auth action error:', error);
        setStatus('error');
        setMessage(getErrorMessage(error));
      }
    };

    handleAuthAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, actionCode]);

  const handleVerifyEmail = async () => {
    try {
      await applyActionCode(auth, actionCode);
      setStatus('info');
      setMessage('Your email has been verified successfully!');
    } catch (error) {
      throw error;
    }
  };

  const handleSignIn = async () => {
    try {
      const emailLink = window.location.href;
      const user = await completePasswordlessSignIn(emailLink);
      
      setStatus('info');
      setMessage(`Welcome back! Signed in as ${user.email}`);
    } catch (error) {
      throw error;
    }
  };

  const getErrorMessage = (error) => {
    switch (error.code) {
      case 'auth/expired-action-code':
        return 'This link has expired. Please request a new sign-in link.';
      case 'auth/invalid-action-code':
        return 'This link is invalid or has already been used. Please request a new one.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/invalid-email':
        return 'Invalid email address.';
      default:
        return error.message || 'An error occurred. Please try again.';
    }
  };

  return (
    <div className="auth-action-container">
      <div className="auth-action-card">
        {/* Talyora Logo/Brand */}
        <div className="auth-action-brand">
          <h1 className="auth-action-logo">Talyora</h1>
        </div>
        
        {status === 'processing' && (
          <div className={`status-icon ${status}`}>
            <div className="spinner"></div>
          </div>
        )}
        
        {status === 'error' && (
          <div className={`status-icon ${status}`}>
            <span className="error-mark">✗</span>
          </div>
        )}
        
        <h2 className="auth-action-title">
          {status === 'processing' && 'Processing...'}
          {status === 'info' && 'All Set!'}
          {status === 'error' && 'Error'}
        </h2>
        
        <p className={`auth-action-message ${status}`}>{message}</p>
        
        {status === 'info' && (
          <button onClick={() => navigate('/')} className="submit-btn">
            Take Me Home
          </button>
        )}
        
        {status === 'error' && (
          <button onClick={() => navigate('/')} className="back-btn">
            Go Back Home
          </button>
        )}
      </div>
    </div>
  );
};

export default AuthAction;
